
import BaseClass from '../core/BaseClass';
import QuestionTypeVersion from './QuestionTypeVersion';
import WidgetTypes from '../core/enums/WidgetTypes';
import TempCache from '../core/enums/TempCache';
import ParticipantIds from '../core/enums/ParticipantIds';
import getItemValues from '../core/hooks/getItemValues';
import IsTransferChatActionExists from '../core/hooks/IsTransferChatActionExists';
import callIfExists from '../core/callIfExists';
import ManageFocusOnKeyEvents from '../core/hooks/ManageFocusOnKeyEvents';
import { ScriptVisitorResponseStatus, ScriptsQuestionType } from '../core/enums/Enum';
import isCallConnectActionNotAvailable from '../core/hooks/isCallConnectActionNotAvailable';
import isLastMessage from '../core/hooks/isLastMessage';
import TempStorageCache from '../core/hooks/TempStorageCache';
import getScriptForIframe from '../core/hooks/getScriptForIframe';
import getStyleForIframe from '../core/hooks/getStyleForIframe';
import ImportQuestypeTemplateFactory from './ImportFactories/ImportQuestypeTemplateFactory';
import AddorRemoveClass from '../core/hooks/AddorRemoveClass';
import observe from '../core/hooks/observe';
import WidgetStates from '../core/hooks/WidgetStates';
import isAcceptKey from '../core/hooks/isAcceptKey';
import removeClass from '../core/hooks/removeClass';
import logIt from '../../logIt';

export default class QuestionTypeRenderer extends BaseClass {
    /**
     1. sets up options along with DOM context
     2. initializes some variables for Q type processing: e.g. preText, template, answers[], etc
    */

    constructor(options) {
        super(options);
        const me = this;

        me.options = options;
        const templates = ImportQuestypeTemplateFactory(me.options);
        Object.assign(this, {
            questionTypeTextTemplate: templates.questionTypeTextTemplate,
            questionTypeTimeTemplate: templates.questionTypeTimeTemplate,
            questionTypeMultipleChoiceAsListTemplate: templates.questionTypeMultipleChoiceAsListTemplate,
            questionTypeMultipleChoiceOptionsTemplate: templates.questionTypeMultipleChoiceOptionsTemplate,
            questionTypeNoneTemplate: templates.questionTypeNoneTemplate,
            questionTypeCalendarDateTemplate: templates.questionTypeCalendarDateTemplate,
            questionTypeTimeOptionsTemplate: templates.questionTypeTimeOptionsTemplate,
            questionTypeTimeCalendarTemplate: templates.questionTypeTimeCalendarTemplate,
            questionTypeMultipleChoiceTemplate: templates.questionTypeMultipleChoiceTemplate
        });
        me.context = options.context;
        me.isBottomSheetSupported = options.isBottomSheetSupported;
        // private variables
        // me.preText = '';//no longer needed in V3
        me.template = null;
        me.answers = [];

        me.CHATLOG_ID = -1;
        // a new property introduced to decouple individual QT handling
        me.renderer = null;
        me.version = options.version ? options.version : QuestionTypeVersion.Classical;
        me.isLiveAgentAvailable = function (option) {
            if (!option) {
                return;
            }
            return option.isTransferToLiveAgent;
        };
        me.isLastMessageOperatorNotAvailable = function (msgObj) {
            if (
                !ApexChat.allHistoryMessages ||
                (ApexChat.allHistoryMessages && ApexChat.allHistoryMessages.length == 0)
            ) {
                //if no messages in history that means message is from Predefined or message before chat start so set its always true
                return true;
            }
            var allChatMsgs = ApexChat.allHistoryMessages.filter(function (message) {
                return message.text === ApexChat.operatorNotAvailableMessage;
            });
            return allChatMsgs.length > 0;
        };
        me.isAgentLastMessage = function (msgObj) {
            if (
                !ApexChat.allHistoryMessages ||
                (ApexChat.allHistoryMessages && ApexChat.allHistoryMessages.length == 0)
            ) {
                //if no messages in history that means message is from Predefined or message before chat start so set its always true
                return true;
            }
            var allAgentMsgs = ApexChat.allHistoryMessages.filter(function (message) {
                return (
                    message.userId != ParticipantIds.pokeMessage &&
                    message.participantDisplayName !== 'Visitor'
                );
            });
            var lastAgentMsgId =
                allAgentMsgs.length > 0 && allAgentMsgs[allAgentMsgs.length - 1].id;
            return lastAgentMsgId == msgObj.id;
        };
    }

    setVersion(ver) {
        this.version = ver;
    }

    isQuestionTypeOptionsAvailable(msgObj) {
        let flag = true;
        switch (msgObj.questionType) {
            case ScriptsQuestionType.TrueOrFalse:
            case ScriptsQuestionType.MultipleChoice:
            case ScriptsQuestionType.AllThatApply: {
                if (msgObj.options == '') {
                    flag = false;
                }
            }
        }
        return flag;
    }

    autoScroll() {
        // Allow a scroller to be set independently of the message container
        const me = this;
        let scroller = me.context.querySelector('#apexchat .apexchat_scroller');
        if (!scroller && me.$) {
            scroller = me.$('#apexchat .apexchat_scroller', me.context);
        }

        if (scroller) {
            scroller.scrollTop = scroller.scrollHeight;
        } else if (me.options && me.options.messagesContainer) {
            me.options.messagesContainer.scrollTop = me.options.messagesContainer.scrollHeight;
        }
    }

    /**
     rendering a Q type onMessageArrived
    */
    async renderMessage(msgObj, callback, cbArg) {
        //todo: refactor this code to separate this function from this class
        const me = this;
        const mqtMeta = { instruction: '' }; // this is the only change in the existing QuestionTypeRenderer codebase: added a JOSN to keep the right instruction text
        const questionTypeDiv = document.createElement('div');
        // apex-css-no-bgColor

        questionTypeDiv.className =
            'apexchat_auto_genned_message_wrapper apexchat_question_type_main_div apex-bot apexchat_message_left';

        questionTypeDiv.id = `apexchat_chatlog_${msgObj.id}`;
        // only PreChat Elements it would be available.
        // if (ApexChat.preChatElement) {
        // TODO: [Refactoring required; need to move these widget specific code inside child classes.].
        if (
            ApexChat.preChatElement &&
            me.state.widget != WidgetTypes.EPUIController &&
            me.state.getStateValue(WidgetTypes.PreChat) === WidgetStates.ShownMaximized
        ) {
            const element = me.$(ApexChat.preChatElement).find('#apexchat_prechat_message')[0];
            element.innerHTML = '';
            me.options.messagesContainer = element;
            ApexChat.preChatElement = null;
        }
        const module = await import(
            /* webpackChunkName: "questionTypeRendererFactory" */ './QuestionTypeRendererFactory'
        );
        const QuestionTypeRendererFactory = module.default;
        me.questionTypeRendererFactory = new QuestionTypeRendererFactory();

        me.renderer = await me.questionTypeRendererFactory.create(
            Object.assign(me.options, {
                version: me.version,
                msg: msgObj,
                wrapper: questionTypeDiv,
                container: me.options.messagesContainer,
                // container: me.options.elements.messagesContainerSelector,
                $: me.$,
                context: me.context,
                applyNewDateTime: me.options.applyNewDateTime,
                isBottomSheetSupported: me.isBottomSheetSupported,
                isFromPreChat:
                    (me.options &&
                        me.options.preChat &&
                        me.options.preChat.msg &&
                        me.options.preChat.msg.isFromPreChat) ||
                    msgObj.isFromPreChat,
                updateCallback() {
                    me.CHATLOG_ID = msgObj.id;
                }
            })
        );

        const data = {
            msgObj,
            questionTypeDiv,
            model: me.options.model
        };
        function renderDateTimeQT(type) {
            if (
                me.options.widgetType !== WidgetTypes.PreChat &&
                me.options.widgetType !== WidgetTypes.VideoGreetInvitation &&
                me.options.model.widget !== WidgetTypes.Windowed &&
                ApexChat.flatPickr === undefined
            ) {
                const lazyLoadScript = getScriptForIframe(
                    'https://d3b4i635mede5k.cloudfront.net/flatpickr.js',
                    'flatpickr_js',
                    me.options.iframeId || me.options.model.iframeId
                );
                const lazyLoadCss = getStyleForIframe(
                    'https://d3b4i635mede5k.cloudfront.net/flatpickr.css',
                    'flatpickr_css',
                    me.options.iframeId || me.options.model.iframeId
                );
                Promise.all([lazyLoadCss, lazyLoadScript]).then((value) => {
                    ApexChat.flatPickr = value[1];
                    if (me.renderer) {
                        me.renderer.render({
                            onSelect: me.sendResponse
                        });
                        mqtMeta.instruction = msgObj.questionPlaceholder || type;
                    }
                    me.options.afterFlatPickerLoaded();
                    me.bindControls(msgObj);
                });
            } else if (me.renderer) {
                me.renderer.render({
                    onSelect: me.sendResponse
                });
                mqtMeta.instruction = msgObj.questionPlaceholder || type;
            }
        }
        const getTimeIntervals = (abbreviation) => {
            const businessStartTime = 9;
            const businessEndTime = 17;
            const data = [];
            for (let i = businessStartTime; i < businessEndTime; i++) {
                const prevtime = `${i > 12 ? i - 12 : i}:00`;
                for (let j = 0; j < 4; j++) {
                    var postfix = ` AM ${abbreviation}`;
                    if (i >= 12) {
                        // 12 pm is noon
                        postfix = ` PM ${abbreviation}`;
                    }
                    const time = `${i == 0 ? 12 : i > 12 ? i - 12 : i}:${j == 0 ? 10 : (j * 15).toString()
                        }${postfix}`;
                    data.push(`${prevtime} to ${time}`);
                }
                data.push(`${prevtime} to ${i + 1 > 12 ? i + 1 - 12 : i + 1}:00${postfix}`);
            }
            return data;
        };

        if (!msgObj.handlerImageUrl && me.options.widgetType == WidgetTypes.ExitPopup) {
            msgObj.handlerImageUrl = me.options?.model?.handlerImageUrl;
        }
        switch (msgObj.questionType) {
            case ScriptsQuestionType.TrueOrFalse:
                if (me.renderer) {
                    me.renderer.render();
                    mqtMeta.instruction = me.getQTPlaceHolder(msgObj.questionType);
                }
                var options = ApexChat.TryParse(data.msgObj.options);
                var yesButton = me.context.getElementById(`apexchat-btn-yes-${msgObj.id}`);
                var noButton = me.context.getElementById(`apexchat-btn-no-${msgObj.id}`);
                IsTransferChatActionExists(options[0], yesButton);
                IsTransferChatActionExists(options[1], noButton);

                logIt(
                    `isLiveAgentAvailable:: in True option case: ${options[0].isTransferToLiveAgent}`
                );
                logIt(
                    `isLiveAgentAvailable:: in False option case: ${options[1].isTransferToLiveAgent}`
                );

                if (
                    yesButton &&
                    options[0].text === msgObj.yesText &&
                    !me.isLiveAgentAvailable(options[0])
                ) {
                    me.$(yesButton)
                        .disable({
                            applyGrayBackground: false,
                            elementQT: yesButton,
                            tabIndex: 0
                        })
                        .addClass('apexchat_disabled_qt_options');
                }
                if (
                    noButton &&
                    options[1].text === msgObj.noText &&
                    !me.isLiveAgentAvailable(options[1])
                ) {
                    me.$(noButton)
                        .disable({ applyGrayBackground: true, elementQT: noButton, tabIndex: 0 })
                        .addClass('apexchat_disabled_qt_options');
                }
                break;
            case ScriptsQuestionType.MultipleChoice:

                // me.options = Object.assign(me.options, ApexChat.getPreloadedChatOptions());
                var template = data.msgObj.questionTypeDisplayListForVisitor
                    ? me.questionTypeMultipleChoiceAsListTemplate
                    : me.questionTypeMultipleChoiceTemplate;
                me.template = template(data.msgObj);

                if (data.msgObj.questionTypeDisplayListForVisitor) {
                    if (me.renderer) {
                        me.renderer.render({
                            onSelect: me.sendResponse
                        });
                        mqtMeta.instruction = me.getQTPlaceHolder(msgObj.questionType);
                    }
                } else {
                    // Fill options div
                    var selectOptions = document.createElement('div');
                    var options = {};
                    if (me.renderer && me.renderer.listOptions) {
                        options = me.renderer.listOptions;
                    } else {
                        options = ApexChat.TryParse(data.msgObj.options);
                    }
                    if (options) {
                        for (var i = 0; i < options.length; i++) {
                            // parse the text in case if there is encoded html coming along
                            let optionDecoded = options[i].option;
                            if (
                                options[i].option.indexOf('&lt;') > -1 ||
                                options[i].option.indexOf('&gt;') > -1
                            ) {
                                optionDecoded = me.$('<div />').html(options[i].option).text();
                            }
                            const styleConfig = me.getOptionStyles(
                                options[i].agentScriptOptionStyles,
                                data.msgObj.id,
                                i + 1
                            );
                            selectOptions.innerHTML += me.questionTypeMultipleChoiceOptionsTemplate({
                                messageId: data.msgObj.id,
                                id: i + 1,
                                name: optionDecoded,
                                styles: styleConfig ? styleConfig.styles : '',
                                fontStyle: styleConfig ? styleConfig.fontStyle : ''
                            });
                        }
                        me.applyTemplate(data.questionTypeDiv, selectOptions);
                        for (var i = 0; i < options.length; i++) {
                            // Handle button click
                            const radioButton = me.context.getElementById(
                                `apexchat_question_type_multiple_choice_radio_parent_option_label_${msgObj.id
                                }_${i + 1}`
                            );
                            var radioButtonParent = me.context.querySelectorAll(
                                `#apexchat_question_type_multiple_choice_options_div_${msgObj.id} .apexchat_question_type_multiple_choice_radio`
                            )[0];
                            if (radioButton) {
                                IsTransferChatActionExists(options[i], radioButton);
                                logIt(
                                    `isLiveAgentAvailable:: in option : ${radioButton.innerText}: ${options[i].isTransferToLiveAgent}`
                                );
                                // attach handlers: mouse and keyboard based
                                if (
                                    isCallConnectActionNotAvailable(options[i]) ||
                                    !me.isLiveAgentAvailable(options[i])
                                ) {
                                    // CHAT-7911 Disabled option if call connect is not available
                                    me.$(radioButton, me.context)
                                        .disable({ applyGrayBackground: true })
                                        .addClass('apexchat_disabled_qt_options');
                                    continue;
                                }
                                observe(
                                    radioButton,
                                    'click',
                                    me.sendResponse.bind2(me, msgObj, options[i].option)
                                );
                                observe(radioButton, 'keyup', function (e) {
                                    if (!me.$(`#${e.target.id}> input:radio`).prop('checked')) {
                                        me.manageQtFocus.bind2(this, e, {
                                            msg: msgObj,
                                            text: e.currentTarget.innerText,
                                            context: me,
                                            eventType: 'sendResponse',
                                            muteElements: ['.apexchat_question_type_update_button']
                                        })(); // muting elements on selector wise
                                    }
                                    if (e.keyCode == 9 || e.code == 'tab') {
                                        radioButtonParent.addClass('apexchat_element_on_focus');
                                    }
                                });
                                observe(radioButton, 'blur', (e) => {
                                    radioButtonParent.classList.remove('apexchat_element_on_focus');
                                });
                            }
                        }
                    }
                    if (me.renderer) {
                        me.renderer.displaySkipOption();
                    }
                    mqtMeta.instruction = me.getQTPlaceHolder(msgObj.questionType);
                }
                break;
            case ScriptsQuestionType.AllThatApply:
                // Select template that appear as check box list vertically when questionTypeDisplayListForVisitor is true
                // Else Select template that appear as bubble style checkboxes
                // me.options = Object.assign(me.options, ApexChat.getPreloadedChatOptions());
                if (data.msgObj.questionTypeDisplayListForVisitor && me.isBottomSheetSupported) {
                    if (me.renderer) {
                        me.renderer.render({
                            onSelect: me.sendResponse
                        });
                        mqtMeta.instruction = me.getQTPlaceHolder(msgObj.questionType);
                    }
                } else if (me.renderer) {
                    me.renderer.render();
                    mqtMeta.instruction = me.getQTPlaceHolder(msgObj.questionType);
                }

                break;
            case ScriptsQuestionType.Text:
                if (msgObj.visitorResponseTypes) {
                    if (me.renderer) {
                        me.renderer.render({
                            onSelect: me.sendResponse.bind2(me)
                        });
                        mqtMeta.instruction = me.getQTPlaceHolder(msgObj.questionType);
                    }
                }
                break;
            case ScriptsQuestionType.FullText: // use this input field for fulltext
                me.template = me.questionTypeTextTemplate(data.msgObj);
                me.applyTemplate(data.questionTypeDiv);
                mqtMeta.isFullText = true;
                mqtMeta.isFromPreChat = true;
                break;
            case ScriptsQuestionType.CalendarDate:
                if (me.isBottomSheetSupported && me.options.applyNewDateTime) {
                    renderDateTimeQT(me.getQTPlaceHolder(msgObj.questionType));
                    break;
                } else {
                    data.msgObj.questionPlaceholder = '';
                    me.template = me.questionTypeCalendarDateTemplate(
                        data.msgObj
                    );
                    me.applyTemplate(data.questionTypeDiv);
                    var calendarInput = me.$(
                        `#apexchat_question_type_time_calendar_input_control_${msgObj.id}`,
                        me.context
                    );
                    ManageFocusOnKeyEvents(calendarInput);
                    mqtMeta.instruction = me.getQTPlaceHolder(msgObj.questionType);
                }
                break;
            case ScriptsQuestionType.Time:
                if (me.isBottomSheetSupported && me.options.applyNewDateTime) {
                    renderDateTimeQT(me.getQTPlaceHolder(msgObj.questionType));
                    break;
                } else {
                    me.template = me.questionTypeTimeTemplate(data.msgObj);
                    const intervals = getTimeIntervals(me.options.questionTypeTimeZoneAbbreviation);
                    // Fill options div
                    var selectOptions = document.createElement('div');
                    if (selectOptions) {
                        for (var i = 0; i < intervals.length; i++) {
                            selectOptions.innerHTML +=
                                me.questionTypeTimeOptionsTemplate(
                                    { name: intervals[i] }
                                );
                        }
                    }
                    me.applyTemplate(data.questionTypeDiv, selectOptions);
                    window.setTimeout(() => {
                        // wait for time input field to render
                        const timeInput = me.$(
                            `#apexchat_question_type_time_select_control_${msgObj.id} .apexchat_dropSelect_elements`,
                            me.context
                        );
                        if (timeInput) {
                            timeInput.addClass('apexchat-element-outline-remove');
                            timeInput.on('keyup blur', (e) => {
                                // apply apexchat-element-outline-remove which help us to hide blue border where unnecessary appeared, blue border should appear when we press tab on particular field
                                if (e.type == 'keyup' && (e.keyCode == 9 || e.code == 'tab')) {
                                    AddorRemoveClass(
                                        timeInput,
                                        'apexchat-element-outline-remove',
                                        false
                                    );
                                }
                                if (e.type == 'blur') {
                                    // when focus removed from element, so blue border should not appear.
                                    AddorRemoveClass(
                                        timeInput,
                                        'apexchat-element-outline-remove',
                                        true
                                    );
                                }
                            });
                        }
                    }, TempCache.RENDER_QT_TIME);
                    mqtMeta.instruction = me.getQTPlaceHolder(msgObj.questionType);
                }

                break;
            case ScriptsQuestionType.TimeCalendar:
                if (me.isBottomSheetSupported && me.options.applyNewDateTime) {
                    renderDateTimeQT(me.getQTPlaceHolder(msgObj.questionType));
                    break;
                } else {
                    me.template =
                        me.questionTypeTimeCalendarTemplate(
                            data.msgObj
                        );
                    const intervals = getTimeIntervals(me.options.questionTypeTimeZoneAbbreviation);
                    // Fill options div
                    var selectOptions = document.createElement('div');
                    if (selectOptions) {
                        for (var i = 0; i < intervals.length; i++) {
                            selectOptions.innerHTML +=
                                me.questionTypeTimeOptionsTemplate(
                                    { name: intervals[i] }
                                );
                        }
                    }
                    me.applyTemplate(data.questionTypeDiv, selectOptions);
                    var calendarInput = me.$(
                        `#apexchat_question_type_time_calendar_input_control_${msgObj.id}`,
                        me.context
                    );
                    ManageFocusOnKeyEvents(calendarInput);
                    ManageFocusOnKeyEvents(calendarInput);
                    window.setTimeout(() => {
                        // wait for time input field to render
                        const timeInput = me.$(
                            `#apexchat_question_type_time_calendar_select_control_${msgObj.id} .apexchat_dropSelect_elements`,
                            me.context
                        );
                        if (timeInput) {
                            timeInput.addClass('apexchat-element-outline-remove');
                            timeInput.on('keyup blur', (e) => {
                                // apply apexchat-element-outline-remove which help us to hide blue border where unnecessary appeared, blue border should appear when we press tab on particular field
                                if (e.type == 'keyup' && (e.keyCode == 9 || e.code == 'tab')) {
                                    AddorRemoveClass(
                                        timeInput,
                                        'apexchat-element-outline-remove',
                                        false
                                    );
                                }
                                if (e.type == 'blur') {
                                    AddorRemoveClass(
                                        timeInput,
                                        'apexchat-element-outline-remove',
                                        true
                                    );
                                }
                            });
                        }
                    }, TempCache.RENDER_QT_TIME);
                    mqtMeta.instruction = me.getQTPlaceHolder(msgObj.questionType);
                    break;
                }
            case ScriptsQuestionType.AppointmentScheduler:
                if (me.renderer) {
                    me.renderer.render({
                        onAutoScroll: me.autoScroll.bind2(me)
                    });
                }
                mqtMeta.instruction = me.getQTPlaceHolder(msgObj.questionType);

                break;
            case ScriptsQuestionType.VideoGreet:
                if (me.renderer) {
                    me.renderer.render();
                }
                break;
            case ScriptsQuestionType.None:
                data.msgObj.userId = data.msgObj.userId || 0;
                me.template = me.questionTypeNoneTemplate(data.msgObj);
                me.applyTemplate(data.questionTypeDiv);
                break;
            default:
                break;
        }
        if (!mqtMeta.isFullText) {
            // bind buttons: Confirm, Skip, Yes, No, Update
            me.bindControls(msgObj);
        }

        let skipButton = me.context.querySelector('.apexchat_skip_input');
        if (skipButton) {
            skipButton.style.display = 'none'; // hide FT skip input button when other QT's skip button displayed
        }
        if (msgObj.questionType == ScriptsQuestionType.Text && msgObj.isFromPreChat) {
            if (ApexChat.ContactInfo) {
                if (!me.$('#apexchat_contact_qt_btn_' + msgObj.id, me.context).is(':visible')) {
                    me.$('#livechat_send_message_button', me.context).trigger('click');
                } else {
                    me.$('#apexchat_contact_qt_btn_' + msgObj.id, me.context).trigger('click');
                }
            }
        } else if (!ApexChat.options.standardInvitationEnabled) {
            me.setVisitorSelection(msgObj);
        }
        ApexChat.storageAPI.Store('questionTypeId', msgObj.id);
        ManageFocusOnKeyEvents(me.context.querySelector('.apexchat_participant_display_text'));
        // update log_id in these scenarios:
        // 1.checking visitor want to update this question,
        // 2. when an anwsered QT is loaded from EP to DOM or
        // 3. an answered QT is loaded after DOM reload
        me.CHATLOG_ID =
            msgObj.visitorResponseStatus == ScriptVisitorResponseStatus.Updating ||
                msgObj.visitorResponseStatus == ScriptVisitorResponseStatus.Answered
                ? msgObj.id
                : -1;
        logIt('plugging custom dd....');
        me.plugCustomDropbown(msgObj);
        // call extra functon newly added to plug new behavior after a message is rendered
        if (callback && typeof callback === 'function') {
            callback({ meta: mqtMeta, msg: msgObj, cb: cbArg });
        }
        // render agent script documents
        if (data.msgObj && data.msgObj.agentScriptDocuments && data.msgObj.isDocument) {
            import(
                /* webpackChunkName: "FilesToQuestionTypesRenderer" */ './FilesToQuestionTypesRenderer'
            ).then((module) => {
                const FilesToQuestionTypesRenderer = module.default;
                const filesToQTTypesRenderer = new FilesToQuestionTypesRenderer(
                    Object.assign(me.options, data)
                );
                filesToQTTypesRenderer.render(data.questionTypeDiv);
            });
        }
    }

    renderSelection(msgObj) {
        // This method is only run for TF selection from preChat CHAT-7045
        const sender = {
            target: {}
        }; // creating a fake object model for sender.
        sender.target.textContent = msgObj.msgText;
        this.renderer && this.renderer.onSelection(sender, msgObj, msgObj.msgText);
    }

    /**
     *  This function is handling the visitor selection of TF, MC, ATA etc question type.
     * @param {msgObj} question type message detail
     */
    async setVisitorSelection(msgObj) {
        const me = this;
        if (isLastMessage(msgObj) && !msgObj.visitorSelectedOptions) {
            // if this QT the v last msg; then it must be unanswered? show it as new-born baby
            return;
        }
        if (me.isLastMessageOperatorNotAvailable(msgObj) && !msgObj.visitorSelectedOptions) {
            // if this QT the v last msg; then it must be unanswered? show it as new-born baby
            return;
        }
        if (
            msgObj.visitorSelectedOptions &&
            msgObj.visitorSelectedOptions.indexOf &&
            msgObj.visitorSelectedOptions.indexOf('\\') != -1
        ) {
            msgObj.visitorSelectedOptions = msgObj.visitorSelectedOptions.replaceAll('\\', '\\\\');
            msgObj.visitorSelectedOptions = JSON.stringify(msgObj.visitorSelectedOptions)
        }
        const selectedOptions = ApexChat.TryParse(msgObj.visitorSelectedOptions);
        if (selectedOptions) {
            me.continueMappingForVisitorSelection(msgObj, selectedOptions);
            //Web to SMS form
            var isWebToSmsOptionSelected =
                isLastMessage(msgObj) && msgObj.questionType == ScriptsQuestionType.MultipleChoice;
            var isSmsBottomSheetOpenedBeforeRefresh =
                selectedOptions[0] &&
                msgObj.questionType == ScriptsQuestionType.MultipleChoice &&
                ApexChat.localStorageAPI.Get('sms_bottomsheet_opened') == 'true';
            if (isWebToSmsOptionSelected || isSmsBottomSheetOpenedBeforeRefresh) {
                const selection = me.getSelectedOption(selectedOptions[0].option, msgObj);
                me.checkConvertToSms(selection);
            }
        }
        // For any past QT whether answered/unanswered: show selection  (if answered), disable (always) and hide Send/Cancel buttons (always)

        const module = await import(
            /* webpackChunkName: "setSelectionOrDisable" */ '../core/hooks/setSelectionOrDisable'
        );
        const setSelectionOrDisable = module.default;
        setSelectionOrDisable(me, msgObj, selectedOptions, true);
        me.hideButtons(msgObj);
        me.hideSkipButton(); // hide skip button when user submit the response.
    }

    hideButtons(msgObj) {
        const me = this;
        // Show Update button for updating response later on
        // var updateButton = me.$('#apexchat-btn-update-' + msgObj.id, me.context);
        // if (updateButton && me.options.isUpdateSupported) {
        //    me.$(updateButton).show();
        // }
        // Hide confirm/okay button
        const confirmButtonDiv = me.$(
            `#apexchat_question_type_div_confirm_button_${msgObj.id}`,
            me.context
        );
        if (confirmButtonDiv) {
            me.$(confirmButtonDiv).hide();
        }
        // hide skip/close buttons
        const closeButtonDiv = me.$(
            `#apexchat_question_type_div_close_button_${msgObj.id}`,
            me.context
        );
        if (closeButtonDiv) {
            me.$(closeButtonDiv).hide();
        }
    }

    continueMappingForVisitorSelection(msgObj, selectedItem) {
        const me = this;
        if (msgObj.shouldContinueMapping) {
            // get QT selected items
            if (typeof selectedItem == 'string') {
                selectedItem = selectedItem.replaceAll('\\\\', '\\\\\\');
                selectedItem = ApexChat.TryParse(selectedItem)
            }
            const selectedQTOptions = me.getQTSelectedOptions(selectedItem);
            // send response to server, bring next mapped question IF ANY.
            const response = {
                chatId: msgObj.chatId,
                isSkipped: false,
                answers: selectedQTOptions.selectedOptions,
                msgText: selectedQTOptions.selectedOptionText,
                agentScriptAnswerId: msgObj.scriptAnswerId,
                chatLogId: msgObj.id,
                shouldRenderResponseAsText:
                    (msgObj.questionTypeDisplayListForVisitor && me.isBottomSheetSupported) ||
                    me.renderQTResponseAsText(msgObj),
                contactInfo: ApexChat.ContactInfo || '',
                questionType: msgObj.questionType
            };
            response.questionTypeChatLogIdToBeUpdated = msgObj.id;
            ApexChat.ContactInfo = '';
            me.options.responseCallback
                ? me.options.responseCallback(response)
                : (function () {
                    console.log('No callback defined for sending Q type response');
                })();
        }
    }

    renderQTResponseAsText(msgObj) {
        const me = this;
        let flag = false;

        switch (msgObj.questionType) {
            case ScriptsQuestionType.TrueOrFalse:
            case ScriptsQuestionType.MultipleChoice:
            case ScriptsQuestionType.AllThatApply:
            case ScriptsQuestionType.TimeCalendar:
            case ScriptsQuestionType.CalendarDate:
            case ScriptsQuestionType.Time:
            case ScriptsQuestionType.Text: {
                flag = true;
            }
        }
        return flag;
    }

    getQTSelectedOptions(selectedItem) {
        let selectedOptions = '';
        let selectedOptionText = '';
        // check selected item length
        if (selectedItem && selectedItem.length > 0) {
            for (let i = 0; i <= selectedItem.length - 1; i++) {
                if (i == 0) {
                    selectedOptions = `[{ "option": "${selectedItem[i].option}"}`;
                    selectedOptionText = selectedItem[i].option;
                } else {
                    selectedOptions += `,{ "option": "${selectedItem[i].option}"}`;
                    selectedOptionText += `,${selectedItem[i].option}`;
                }
            }
            selectedOptions += ']';
        }

        return { selectedOptions, selectedOptionText };
    }

    //* *// insert attached files with MC options */
    applyMCWithFileTemplate(questionTypeDiv, selectOptions, files) {
        var me = this;
        questionTypeDiv.innerHTML = selectOptions
            ? me.template.replace('apexchat_options_div', selectOptions.innerHTML)
            : me.template;
        // if (files){

        //     questionTypeDiv.innerHTML = (files) ? questionTypeDiv.innerHTML.replace('apexchat_files_div', files.innerHTML) : me.template;
        // } else {
        //     questionTypeDiv.innerHTML = questionTypeDiv.innerHTML.replace('apexchat_files_div', '')
        // }
        me.options.messagesContainer.appendChild(questionTypeDiv);
    }

    applyTemplate(questionTypeDiv, selectOptions) {
        // Apply template
        const me = this;
        questionTypeDiv.innerHTML = selectOptions
            ? me.template.replace('apexchat_options_div', selectOptions.innerHTML)
            : me.template;
        me.options.messagesContainer.appendChild(questionTypeDiv);
    }

    /**
        Clears UI after sending response
      */
    hideAll(id, context) {
        const me = this;
        context = context || me.context;
        // TrueOrFalse - btns
        me.$(`#apexchat-btn-yes-${id}`, context).hide();
        me.$(`#apexchat-btn-no-${id}`, context).hide();
        // MultipleChoice- btns
        me.$(`#apexchat_question_type_multiple_choice_options_div_${id}`, context).hide();
        // general - btns
        me.$(`#apexchat_question_type_div_confirm_button_${id}`, context).hide();
        me.$(`#apexchat_question_type_div_close_button_${id}`, context).hide();
        // All that Apply
        me.$(`#apexchat_question_type_all_that_apply_options_div_${id}`, context).hide();
        // Text
        me.$(`#apexchat_question_type_text_input_${id}`, context).hide();
        // Calender Date
        me.$(`#apexchat_question_type_calendar_input_${id}`, context).hide();
        // Time
        me.$(`#apexchat_question_type_time_${id}`, context).hide();
        // TimeCalendar
        me.$(`#apexchat_question_type_time_calendar_input_div_${id}`, context).hide();
        me.$(`#apexchat_question_type_time_calendar_select_div_${id}`, context).hide();
        // when closing prechat msg IFrame should resize.
        me.postMessage({
            event: 'prechat-auto-resizing',
            data: {
                isResetIframe: true,
                hidePreChatMessages: true,
                isSingleMsg: true,
                hideCloseButton: true // we need to hide close button
            }
        });
    }

    manageQtFocus(e, options) {
        const me = this;
        var doOnAcceptKey =
            options && options.onAccept
                ? options.onAccept
                : function () {
                    /* do nothing */
                };
        if (isAcceptKey(e)) {
            if (!options.context.$(this, options.context.context).isDisabled()) {
                // if element is disabled it should not work using keyboard
                options.isNotSend
                    ? doOnAcceptKey.bind2(me, e, options)()
                    : options.context[options.eventType](e, options.msg, options.text);
                options.context.$(this, options.context.context).blur();
                // we prevent vox to read element.
                options.context.muteAllElements(options);
                setTimeout(() => {
                    // we enabled vox to read element.
                    options.context.unmuteAllElements(options);
                }, 500);
            }
        }
        // else {
        //    ApexChat.manageFocus(e, this);
        // }
    }

    /*
        Using this function we prevent vox to read element. 
     */
    muteAllElements(options) {
        for (let i = 0; i < options.muteElements.length; i++) {
            options.context
                .$(options.muteElements[i], options.context.context)
                .attr('aria-hidden', true);
        }
    }

    /*
        Using this function we enabled vox to read element. 
     */
    unmuteAllElements(options) {
        for (let i = 0; i < options.muteElements.length; i++) {
            options.context
                .$(options.muteElements[i], options.context.context)
                .attr('aria-hidden', false);
        }
    }

    /**
      
     does the job of event-binding of buttons: Yes/No/Confirm/Skip/Update
    */
    onUpdateEvent(sender, message, callback) {
        const me = this;
        if (message.questionType === ScriptsQuestionType.TrueOrFalse) {
            const yesButton = me.context.getElementById(`apexchat-btn-yes-${message.id}`);
            if (yesButton) {
                yesButton.style.display = 'block';
                yesButton.style.pointerEvents = 'auto';
            }
            const noButton = me.$(`#apexchat-btn-no-${message.id}`, me.context);
            if (noButton) {
                me.$(noButton).show();
                me.$(noButton).css('pointer-events', 'auto');
            }
            callIfExists(callback, me, message);
        } else if (message.questionType === ScriptsQuestionType.MultipleChoice) {
            var mainDiv = me.$(
                `#apexchat_question_type_multiple_choice_options_div_${message.id}`,
                me.context
            );
            var okBtn = me.$(
                `#apexchat_question_type_div_confirm_button_${message.id}`,
                me.context
            );
            const idSelectControl = me.$(
                `#apexchat_question_type_multiple_choice_as_list_select_control_${message.id}`,
                me.context
            );
            if (mainDiv) {
                me.$(mainDiv).show();
                var children = mainDiv.children();
                for (var i = 0; i < children.length; i++) {
                    var childOption = children[i];
                    // me.$(childOption).children('input:checked').each(function () {
                    //     me.$(this).prop('checked', false);
                    // });
                    me.$(childOption).show();
                }
                if (okBtn) {
                    me.$(okBtn).show();
                }
                if (idSelectControl) {
                    idSelectControl.enable();
                }
            }
        } else if (message.questionType === ScriptsQuestionType.AllThatApply) {
            var mainDiv = me.$(
                `#apexchat_question_type_all_that_apply_options_div_${message.id}`,
                me.context
            );
            var okBtn = me.$(
                `#apexchat_question_type_div_confirm_button_${message.id}`,
                me.context
            );
            if (mainDiv) {
                me.$(mainDiv).show();
                var children = mainDiv.children();
                for (var i = 0; i < children.length; i++) {
                    var childOption = children[i];
                    // me.$(childOption).children('input:checked').each(function () {
                    //     me.$(this).prop('checked', false);
                    // });
                    me.$(childOption).show();
                }
                // enable all that apply options
                me.$(mainDiv).enable();
            }
            if (okBtn) {
                me.$(okBtn).show();
            }
        }
        // Set chat log id of Question Type in order to send it alongwith chosen answer in order to udating the last meassage;
        me.CHATLOG_ID = message.id;
        // hiding update button
        const confirmButton = me.context.getElementById(`apexchat-btn-confirm-${message.id}`);
        // checking okay button exist in quick reply Make send button behave like QuickReply Okay button
        if (confirmButton) {
            if (me.options.sendButton) {
                const sendBtn = me.options.sendButton;
                TempStorageCache.add('QuestionTypeCurrentMessage', message);
                TempStorageCache.add('QuestionTypeRenderer', me);
                observe(sendBtn, 'click', me.sendBtnClick);
            }
        }
        me.$(sender.currentTarget).parent().hide();
        // indicate at DB that visitor is clearing last selections for updating
        //  me.answers.push({ option: 'cancelQtAns' });
        //  me.clearToUpdate(message);
    }

    sendBtnClick(e) {
        const me = TempStorageCache.get('QuestionTypeRenderer'); // using stored context of 'me' as on click we lose the context
        if (me) {
            // check me exists or not
            me.sendResponse(e, TempStorageCache.get('QuestionTypeCurrentMessage'), '', false);
        }
    }

    bindControls(msgObj) {
        //todo: refactor this function
        // Confirm btton for question type handling
        const me = this;
        const confirmButton = me.context.getElementById(`apexchat-btn-confirm-${msgObj.id}`);
        if (confirmButton) {
            observe(confirmButton, 'click', me.sendResponse.bind2(me, msgObj, '', false));
            // apply ADA
            observe(confirmButton, 'keyup', function (e) {
                me.manageQtFocus.bind2(this, e, {
                    msg: msgObj,
                    context: me,
                    eventType: 'sendResponse',
                    muteElements: ['.apexchat_question_type_update_button']
                })(); // muting elements on selector wise
                // if (isAcceptKey(e)) {
                //    me.sendResponse.bind2(me, e, msgObj)();
                // } else {
                //    //ApexChat.manageFocus(e, this);
                // }
            });
            ManageFocusOnKeyEvents(confirmButton);
            // Make send button behave like QuickReply Okay button;ref. CHAT-4294
            if (me.options.sendButton && isLastMessage(msgObj)) {
                const sendBtn = me.options.sendButton;
                TempStorageCache.add('QuestionTypeCurrentMessage', msgObj);
                TempStorageCache.add('QuestionTypeRenderer', me);
                // if response is submitted successfully from the prechat
                // don't need to bind again click event on the send button
                // only need to check ATA Non List QT's
                if (msgObj && msgObj.questionType == ScriptsQuestionType.AllThatApply) {
                    const selectedOptions = ApexChat.TryParse(msgObj.visitorSelectedOptions);
                    if (!selectedOptions && !me.options.model.aiBotEnabled) {
                        observe(sendBtn, 'click', me.sendBtnClick);
                    }
                } else {
                    observe(sendBtn, 'click', me.sendBtnClick);
                }
            }
        }
        const skipButton = me.context.getElementById(`apexchat_skip_question_${msgObj.id}`);
        if (skipButton) {
            observe(
                skipButton,
                'click',
                me.sendResponse.bind2(me, msgObj, "Visitor didn't answer this question!", true)
            );
            // apply ADA
            observe(skipButton, 'keyup', (e) => {
                if (isAcceptKey(e)) {
                    me.sendResponse.bind2(
                        me,
                        e,
                        msgObj,
                        "Visitor didn't answer this question!",
                        true
                    )();
                } else {
                    // ApexChat.manageFocus(e, this);
                }
            });
        }
        const yesButton = me.context.getElementById(`apexchat-btn-yes-${msgObj.id}`);
        if (yesButton) {
            observe(yesButton, 'click', me.sendResponse.bind2(me, msgObj, msgObj.yesText));
            observe(yesButton, 'keyup', function (e) {
                if (me.$(this).css('pointer-events') != 'none') {
                    me.manageQtFocus.bind2(this, e, {
                        msg: msgObj,
                        text: msgObj.yesText,
                        context: me,
                        eventType: 'sendResponse',
                        muteElements: [
                            '.apexchat_question_type_update_button',
                            '.apexchat_question_type_true_or_false'
                        ]
                    })(); // muting elements on selector wise
                }
            });
            ManageFocusOnKeyEvents(yesButton);
        }
        const noButton = me.context.getElementById(`apexchat-btn-no-${msgObj.id}`);
        if (noButton) {
            observe(noButton, 'click', me.sendResponse.bind2(me, msgObj, msgObj.noText));
            observe(noButton, 'keyup', function (e) {
                if (me.$(this).css('pointer-events') != 'none') {
                    me.manageQtFocus.bind2(this, e, {
                        msg: msgObj,
                        text: msgObj.noText,
                        context: me,
                        eventType: 'sendResponse',
                        muteElements: [
                            '.apexchat_question_type_update_button',
                            '.apexchat_question_type_true_or_false'
                        ]
                    })(); // muting elements on selector wise
                }
            });
            ManageFocusOnKeyEvents(noButton);
        }
        // Hide initially
        const updateButtonId = `apexchat-btn-update-${msgObj.id}`;
        const updateBtnContainer = me.context.getElementById(updateButtonId);
        const updateButton = me.context.querySelector(
            `#apexchat-btn-update-${msgObj.id} .apexchat_question_type_update_button`
        );
        if (updateBtnContainer) {
            observe(updateButton, 'click', me.onUpdateEvent.bind2(me, msgObj));
            updateBtnContainer.style.display = 'none';
            // apply ADA
            observe(updateButton, 'keyup', function (e) {
                me.manageQtFocus.bind2(this, e, {
                    msg: msgObj,
                    context: me,
                    eventType: 'onUpdateEvent',
                    muteElements: [`#apexchat-btn-confirm-${msgObj.id}`]
                })(); // muting elements on selector wise
                // if (isAcceptKey(e)) {
                //    me.onUpdateEvent.bind2(me, e, msgObj)();
                // } else {
                //    //ApexChat.manageFocus(e, this);
                // }
            });
        }
    }
    sendResponse(sender, message, text, isSkipped) {
        sender.stopPropagation();
        sender.preventDefault(); // hack for fire fox
        var currentTarget = sender.currentTarget;
        var target = sender.target;
        import(
            /* webpackChunkName: "sendQuetionTypeResponse" */ '../core/hooks/sendQuetionTypeResponse'
        ).then((module) => {
            const sendQuetionTypeResponse = module.default;
            var sender2 = { ...sender };
            sender2.currentTarget = currentTarget;
            sender2.target = target;
            sendQuetionTypeResponse(this, sender2, message, text, isSkipped);
        });
    }
    bindUpdateClick(msgObj, clickHandler) {
        const me = this;
        const updateButtonId = `apexchat-btn-update-${msgObj.id}`;
        const updateButton = me.context.getElementById(updateButtonId);
        if (updateButton) {
            observe(
                updateButton,
                'click',
                me.onUpdateEvent.bind2(me, msgObj, clickHandler)
            );
            // me.$(updateButton).hide();
        }
    }

    /**
      responses and then call the responseCallback passing that response
    */

    clearToUpdate(message) {
        const me = this;
        const response = {
            chatId: me.options.chatId,
            agentScriptAnswerId: message.scriptAnswerId,
            chatLogId: message.id,
            isUpdating: true
        };
        // make selected option part of Q-type response
        response.answers = JSON.stringify(me.answers);
        // make selected option flat string text for DB dsiplay area
        response.msgText = getItemValues(me.answers);
        // CHATLOG_ID will be set 0 in case of first time chosen answer of QT
        if (me.CHATLOG_ID == -1) {
            me.CHATLOG_ID = 0;
        }
        response.questionTypeChatLogIdToBeUpdated = me.CHATLOG_ID;
        me.options.responseCallback
            ? me.options.responseCallback(response)
            : (function () {
                console.log('No callback defined for sending Q type update');
            })();
        // reset and clear things
        me.answers = [];
        $txt.val('');
    }

    /**
       disables or hides Question Type controls for any further interaction after submit
     */
    afterSubmit() {
        const me = this;
        me.$('.update_dot', me.context).each(function (index) {
            me.$(this).hide();
        });
        me.$('.apexchat_question_type_update_button', me.context).each(function (index) {
            me.$(this).hide().parent().hide();
        });
        me.$('.apexchat_question_type_styled_input_container', me.context).each(function (index) {
            me.$(this).disable();
        });
        me.$('.apexchat_dynamic_question_type_input_controls', me.context).each(function (index) {
            me.$(this).disable();
        });
        me.$('.apexchat_question_confirm_or_skip_padding', me.context).each(function (index) {
            me.$(this).hide();
        });
        me.$('.apexchat_contact_qt_wrapper', me.context).hide();
        me.$('.apexchat_question_type_true_or_false', me.context).each(function (index) {
            me.$(this).disable({ recursively: true });
        });
        me.hideSkipButton(); // hide skip button when user submit the response.
    }

    hideSkipButton() {
        const me = this;
        if (me.options && me.options.msg && me.options.msg.isSkippable) {
            const skipButton = me.$(`#apexchat_skip_question_${me.options.msg.id}`, me.context);
            if (me.$(skipButton).is(':visible')) {
                skipButton.hide(); // hide skip button
            }
        }
    }

    // remove old classes and plugin new control; ref. CHAT-4778
    plugCustomDropbown(m) {
        const me = this;
        // MC
        removeClass(
            me.context.getElementById(`apexchat_question_type_multiple_choice_options_div_${m.id}`),
            'apexchat_question_scripts_multiple_choice_as_list'
        );
        removeClass(
            me.context.getElementById(
                `apexchat_question_type_multiple_choice_as_list_select_control_${m.id}`
            ),
            'apexchat_dynamic_question_type_input_controls'
        );
        me.context.getElementById(
            `apexchat_question_type_multiple_choice_as_list_select_control_${m.id}`
        );
        // TODO
        // .apexChatDropSelect({
        //     context: me.context,
        //     className: 'apexchat_question_type_dropSelect'
        // });
        // Time
        removeClass(
            me.context.getElementById(`apexchat_question_type_time_select_control_${m.id}`),
            'apexchat_dynamic_question_type_input_controls'
        );
        removeClass(
            me.context.getElementById(`apexchat_question_type_time_${m.id}`),
            'apexchat_question_scripts_time'
        );
        // TODO
        // me.$(`#apexchat_question_type_time_select_control_${m.id}`, me.context).apexChatDropSelect({
        //     context: me.context,
        //     className: 'apexchat_question_type_dropSelect',
        //     width: '250px'
        // });

        // Time of Calender
        removeClass(
            me.context.getElementById(
                `apexchat_question_type_time_calendar_select_control_${m.id}`
            ),
            'apexchat_dynamic_question_type_input_controls'
        );
        removeClass(
            me.context.getElementById(`apexchat_question_type_time_calendar_select_div_${m.id}`),
            'apexchat_question_scripts_time_calendar'
        );
        //TODO
        // me.$(
        //     `#apexchat_question_type_time_calendar_select_control_${m.id}`,
        //     me.context
        // ).apexChatDropSelect({
        //     context: me.context,
        //     className: 'apexchat_question_type_dropSelect',
        //     width: '250px'
        // });

        // to hide box, uncomment below
        // var apexchat_question_type_single_col = $('#apexchat_question_type_multiple_choice_options_div_' + m.id).parent();
        // ApexChat.addClass(apexchat_question_type_single_col[0], 'apexchat_transparent_question_type');
    }

    setContext(context) {
        this.context = context;
    }

    getContext() {
        return this.context;
    }

    getContextId() {
        return this.context.querySelector('div').id;
    }

    /**
     * After selection of multiple choice, hide last selected RD MC.
     * @param {object} msg- Message object
     */
    afterMCSelectionRD(msg) {
        const me = this;
        if (msg.questionType === ScriptsQuestionType.MultipleChoice) {
            const mcRadioContainer = me.$(
                '.apexchat_question_type_styled_input_container',
                me.context
            );
            const mcRadioLastContainer = mcRadioContainer.length - 1;
            if (mcRadioContainer[mcRadioLastContainer]) {
                mcRadioContainer[mcRadioLastContainer].remove();
            }
        }
    }

    isLastUnAnsweredMessage(msgObj, selectedOptions) {
        const me = this;
        const isLastMsg = me.isAgentLastMessage(msgObj);
        // checking visitor response
        if (
            (msgObj.visitorResponseStatus == ScriptVisitorResponseStatus.UnAnswered ||
                msgObj.visitorResponseStatus == ScriptVisitorResponseStatus.Updating) &&
            isLastMsg
        ) {
            return true;
        }
        // checking visitor select any option
        if (!msgObj.visitorSelectedOptions && isLastMsg) {
            return true;
        }
        // checking & validation of JSON as checking length
        if ((!selectedOptions || selectedOptions.length == 0) && isLastMsg) {
            return true;
        }
        return false;
    }
}
